"use client";

import Cookies from "js-cookie";
import { useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import Spinner from "@/components/common/Spinner";
import styles from "@/assets/css/pages/font.module.css";
import useProductListing from "@/lib/hooks/useProductListing";
import { useAddToTrolley } from "@/lib/hooks/useAddToTrolley";
import { no_products_found, viewItemListAnalytics } from "@/lib/gtm";
import { getPageType, formatProductCategoriesGA4 } from "@/lib/utils/functions";
import {
  pageSummaryInfo,
  createItemListPayload,
} from "@/lib/utils/helpers/plp";

const NoListingItem = dynamic(() => import("@/components/misc/NoListingItem"));
const Item = dynamic(() => import("@/components/common/card/SubCategoryCard"));
const Description = dynamic(() =>
  import("@/components/common/shared/CategoryDescription"),
);
const Pagination = dynamic(() =>
  import("@/components/common/CustomPagination"),
);
const SelectionBox = dynamic(() => import("@/components/common/SelectionBox"), {
  ssr: false,
});

const TierProductList = ({
  items,
  pageSize,
  category,
  short_desc,
  currentURL,
  cookieKeys,
  imageTypes,
  serviceRes,
  storageKeys,
  searchParams,
  catChildCount,
  sortingOptions,
  pageSizeOptions,
}) => {
  const { handleAddToTrolley } = useAddToTrolley();
  const {
    data: {
      filters,
      plpTitle,
      products,
      sortOrder,
      pageLimit,
      isFetching,
      currentPage,
      filterOptions,
      totalProducts,
    },
    methods: {
      t,
      handlePageChange,
      handlePageSelection,
      handleFilterSelection,
      handleSortingSelection,
    },
  } = useProductListing({
    pageSize,
    cookieKeys,
    serviceRes,
    storageKeys,
    searchParams,
    categoryIdParam: true,
    categoryId: category.id,
    initial: {
      page: items.page,
      products: items.data,
      product_total: items.total,
      sort_order: items.sortingOrder,
      filter_options: items.filterOptions,
    },
  });

  useEffect(() => {
    if (totalProducts > 0) {
      const formattedProducts = products.map((product, i) => {
        const brands = product?.brands || [];
        const selectedBrand = brands.find((b) => b.is_selected);
        const productInfo = {
          item_id: selectedBrand?.sku,
          item_name: selectedBrand?.base_title,
          discount: selectedBrand?.amountOff_without_currency,
          item_brand: selectedBrand?.title,
          price: selectedBrand?.final_price_without_currency,
          quantity: 1,
          index: i,
          ...(Number(selectedBrand?.amountOff_without_currency) &&
            !selectedBrand?.specialCoupon_applied && {
              coupon: Cookies.get("specialCoupon"),
            }),
          ...formatProductCategoriesGA4(selectedBrand?.breadcrumbs),
        };
        return productInfo;
      });

      viewItemListAnalytics(
        createItemListPayload(formattedProducts, "Category Products", true),
      );
    } else {
      no_products_found({
        page_type: getPageType(),
      });
    }
  }, [totalProducts]);

  const categoryInfo = useMemo(() => {
    const catTree = category?.breadcrumbs || [];
    const parentId = catTree[catTree.length - 1]?.category_id || null;
    return {
      parentId,
      currentId: category?.id,
    };
  }, [category?.id]);

  return (
    <section className="accessories-subcategories">
      <div className="container">
        <div className="row">
          <div className="col">
            <h2
              className={`${styles.heading32} capitalize small-heading-text title`}
            >
              {catChildCount > 0 && t("common:all")} {plpTitle} {category?.name}
            </h2>
          </div>
        </div>
      </div>
      {catChildCount === 0 && (
        <Description description={short_desc} is_top_desc={true} />
      )}
      {products?.length > 0 ? (
        <>
          <SelectionBox
            filters={filters}
            pageSize={pageLimit}
            sortOrder={sortOrder}
            currentPage={currentPage}
            totalItems={totalProducts}
            sortOptions={sortingOptions}
            filterOptions={filterOptions}
            pageSizeOptions={pageSizeOptions}
            handlePageChange={handlePageChange}
            handlePageSelection={handlePageSelection}
            handleFilterSelection={handleFilterSelection}
            handleSortingSelection={handleSortingSelection}
          />
          <div className="container xs:mb-[80px] mb-[40px] min-h-[70vh]">
            <div className="row xs:mt-24 xs:mb-24 mt-10 mb-12">
              <div className="col w-12/12">
                <p className="text-primary-black-600 xs:text-body-text-3 text-body-text-3-m my-auto block w-12/12 mb-[6px]">
                  {t(
                    "filters.result_summary",
                    pageSummaryInfo(currentPage, pageLimit, totalProducts),
                  )}
                </p>
              </div>
            </div>
            <div className="row" id="product-list">
              <div className="col">
                <div className="grid lg:grid-cols-4 xs:grid-cols-3 grid-cols-2 xs:gap-6 gap-4">
                  {products?.map?.((product, i) => (
                    <Item
                      t={t}
                      loop_index={i}
                      product={product}
                      imageTypes={imageTypes}
                      item_list="Category Products"
                      key={`${product.article_number}${i}`}
                      handleAddToTrolley={handleAddToTrolley}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="row mt-4">
              <div className="col">
                <div className="plp-pagination-box w-12/12">
                  <p className="text-primary-black-600 xs:text-body-text-3 text-body-text-3-m my-auto block w-12/12 mb-[6px]">
                    {t(
                      "filters.result_summary",
                      pageSummaryInfo(currentPage, pageLimit, totalProducts),
                    )}
                  </p>
                  <Pagination
                    scrollToTop
                    page={currentPage}
                    total={totalProducts}
                    currentURL={currentURL}
                    itemsPerPage={pageLimit}
                    setPage={handlePageChange}
                    searchParams={searchParams}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      ) : isFetching ? (
        <div className="flex justify-center w-full h-full top-0 z-[50] blur-backdrop">
          <Spinner />
        </div>
      ) : (
        <NoListingItem
          parentCategoryId={categoryInfo.parentId}
          currentCategoryId={categoryInfo.parentId}
          heading={t("messages.no_results.specific_product")}
          message={t("messages.no_results.specific_product_text")}
          className={`container mt-4 ${isFetching ? "hidden" : ""}`}
        />
      )}
    </section>
  );
};

export default TierProductList;
